export class CrawlerPageStore {
	constructor(localStorageService, ruleRunner, getHomeUrl, blockedWpPathPrefixes = [], blockedWpExactPaths = []) {
		this.localStorageService = localStorageService;
		this.ruleRunner = ruleRunner;
		this.getHomeUrl = typeof getHomeUrl === 'function' ? getHomeUrl : () => window.location.origin;
		this.blockedWpPathPrefixes = Array.isArray(blockedWpPathPrefixes) ? blockedWpPathPrefixes : [];
		this.blockedWpExactPaths = Array.isArray(blockedWpExactPaths) ? blockedWpExactPaths : [];
	}

	normalizeUrl(rawUrl, baseUrl = this.getHomeUrl()) {
		try {
			const parsedUrl = new URL(rawUrl, baseUrl);
			parsedUrl.hash = '';

			const normalizedPath = parsedUrl.pathname.replace(/\/$/, '');
			parsedUrl.pathname = normalizedPath === '' ? '/' : normalizedPath;

			return parsedUrl.toString();
		} catch {
			return '';
		}
	}

	isCrawlableInternalUrl(url) {
		try {
			const targetUrl = new URL(url);
			const homeUrl = new URL(this.getHomeUrl());
			const pathname = targetUrl.pathname;

			if (targetUrl.origin !== homeUrl.origin) {
				return false;
			}

			if (this.blockedWpPathPrefixes.some((blockedPrefix) => pathname.includes(blockedPrefix))) {
				return false;
			}

			if (this.blockedWpExactPaths.some((blockedPath) => pathname.endsWith(blockedPath))) {
				return false;
			}

			return true;
		} catch {
			return false;
		}
	}

	extractLinks(html, baseUrl) {
		const parser = new DOMParser();
		const documentNode = parser.parseFromString(html, 'text/html');
		const anchorNodes = Array.from(documentNode.querySelectorAll('a[href]'));
		const urls = new Set();

		for (const anchorNode of anchorNodes) {
			const href = anchorNode.getAttribute('href');

			if (!href) {
				continue;
			}

			const normalizedUrl = this.normalizeUrl(href, baseUrl);

			if (normalizedUrl && this.isCrawlableInternalUrl(normalizedUrl)) {
				urls.add(normalizedUrl);
			}
		}

		return Array.from(urls);
	}

	normalizePostId(value) {
		const parsedPostId = Number.parseInt(String(value), 10);

		if (!Number.isInteger(parsedPostId) || parsedPostId <= 0) {
			return null;
		}

		return parsedPostId;
	}

	normalizeCrawledAt(value) {
		if (typeof value !== 'string' || value.length === 0) {
			return '';
		}

		const parsed = new Date(value);
		return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString();
	}

	normalizePageType(value) {
		const normalized = String(value || '').toLowerCase();

		if (normalized === 'home' || normalized === 'archive' || normalized === 'singular') {
			return normalized;
		}

		return 'singular';
	}

	normalizeCrawledPages(pages) {
		if (!Array.isArray(pages)) {
			return [];
		}

		return pages
			.filter((page) => typeof page?.url === 'string' && page.url.length > 0)
			.map((page) => ({
				url: page.url,
				depth: Number.isFinite(page.depth) ? page.depth : 0,
				postId: this.normalizePostId(page.postId),
				pageType: this.normalizePageType(page.pageType),
				crawledAt: this.normalizeCrawledAt(page.crawledAt)
			}));
	}

	loadCrawledPages(crawledPathsStorageKey, rulesResultsStorageKey) {
		const parsed = this.localStorageService.getJson(crawledPathsStorageKey, []);

		if (Array.isArray(parsed) && parsed.length > 0) {
			return this.normalizeCrawledPages(parsed);
		}

		const fallbackRulesPages = this.localStorageService.getJson(rulesResultsStorageKey, []);
		return this.normalizeCrawledPages(fallbackRulesPages);
	}

	loadRuleResultPages(rulesResultsStorageKey) {
		const parsed = this.localStorageService.getJson(rulesResultsStorageKey, []);

		if (!Array.isArray(parsed)) {
			return [];
		}

		return this.ruleRunner ? this.ruleRunner.normalizePages(parsed) : parsed;
	}

	getRuleDetailsIndexStorageKey(rulesResultsDetailsStorageKey) {
		return `${String(rulesResultsDetailsStorageKey || '').trim()}__keys`;
	}

	buildRuleDetailsPageStorageKey(rulesResultsDetailsStorageKey, normalizedPageUrl) {
		const normalizedStorageKey = String(rulesResultsDetailsStorageKey || '').trim();
		const normalizedUrl = String(normalizedPageUrl || '').trim();

		if (!normalizedStorageKey || !normalizedUrl) {
			return '';
		}

		return `${normalizedStorageKey}:${encodeURIComponent(normalizedUrl)}`;
	}

	getRuleDetailsPageStorageKey(rulesResultsDetailsStorageKey, pageUrl) {
		const rawPageUrl = String(pageUrl || '').trim();
		const normalizedPageUrl = this.normalizeUrl(rawPageUrl, this.getHomeUrl()) || rawPageUrl;

		return this.buildRuleDetailsPageStorageKey(rulesResultsDetailsStorageKey, normalizedPageUrl);
	}

	getRuleDetailsPageStorageKeyCandidates(rulesResultsDetailsStorageKey, pageUrl) {
		const rawPageUrl = String(pageUrl || '').trim();
		const normalizedPageUrl = this.normalizeUrl(rawPageUrl, this.getHomeUrl()) || rawPageUrl;
		const candidates = [
			this.buildRuleDetailsPageStorageKey(rulesResultsDetailsStorageKey, normalizedPageUrl),
			this.buildRuleDetailsPageStorageKey(rulesResultsDetailsStorageKey, rawPageUrl)
		].filter((candidate) => typeof candidate === 'string' && candidate.length > 0);

		return Array.from(new Set(candidates));
	}

	decodeRuleDetailsPageUrl(detailsStorageKey, rulesResultsDetailsStorageKey) {
		const normalizedStorageKey = String(rulesResultsDetailsStorageKey || '').trim();
		const rawStorageKey = String(detailsStorageKey || '').trim();
		const keyPrefix = `${normalizedStorageKey}:`;

		if (!normalizedStorageKey || !rawStorageKey.startsWith(keyPrefix)) {
			return '';
		}

		try {
			return decodeURIComponent(rawStorageKey.slice(keyPrefix.length));
		} catch {
			return '';
		}
	}

	hasRuleDataBundle(page) {
		const hasRuleDetails = Boolean(
			page &&
			typeof page.ruleDetails === 'object' &&
			page.ruleDetails !== null &&
			Object.values(page.ruleDetails).some((detail) => detail && typeof detail === 'object')
		);
		const hasFailedSelectors = Boolean(
			page &&
			typeof page.failedSelectorsByRule === 'object' &&
			page.failedSelectorsByRule !== null &&
			Object.values(page.failedSelectorsByRule).some((selectors) => Array.isArray(selectors) && selectors.length > 0)
		);
		const hasRuleParts = Boolean(
			page &&
			typeof page.rulePartsByRule === 'object' &&
			page.rulePartsByRule !== null &&
			Object.values(page.rulePartsByRule).some((parts) => {
				if (!parts || typeof parts !== 'object') {
					return false;
				}

				return (
					(parts.header || 0) > 0 ||
					(parts.footer || 0) > 0 ||
					(parts.page || 0) > 0 ||
					(Array.isArray(parts.labels) && parts.labels.length > 0)
				);
			})
		);

		return hasRuleDetails || hasFailedSelectors || hasRuleParts;
	}

	hasRuleDetails(page) {
		return Boolean(
			page &&
			typeof page.ruleDetails === 'object' &&
			page.ruleDetails !== null &&
			Object.values(page.ruleDetails).some((detail) => detail && typeof detail === 'object')
		);
	}

	async loadRuleDataBundleForPage(rulesResultsDetailsStorageKey, pageUrl) {
		const candidateStorageKeys = this.getRuleDetailsPageStorageKeyCandidates(rulesResultsDetailsStorageKey, pageUrl);
		let parsed = null;

		for (const detailsStorageKey of candidateStorageKeys) {
			parsed = await this.localStorageService.getJsonAsync(detailsStorageKey, null);

			if (parsed && typeof parsed === 'object') {
				break;
			}
		}

		if ( ! parsed || typeof parsed !== 'object' ) {
			const normalizedTargetUrl = this.normalizeUrl(pageUrl, this.getHomeUrl()) || String(pageUrl || '').trim();
			const indexStorageKey = this.getRuleDetailsIndexStorageKey(rulesResultsDetailsStorageKey);
			const indexedDetailsKeys = await this.localStorageService.getJsonAsync(indexStorageKey, []);

			for (const indexedDetailsKey of Array.isArray(indexedDetailsKeys) ? indexedDetailsKeys : []) {
				const decodedIndexedUrl = this.decodeRuleDetailsPageUrl(indexedDetailsKey, rulesResultsDetailsStorageKey);
				const normalizedIndexedUrl = this.normalizeUrl(decodedIndexedUrl, this.getHomeUrl()) || decodedIndexedUrl;

				if (!normalizedIndexedUrl || normalizedIndexedUrl !== normalizedTargetUrl) {
					continue;
				}

				parsed = await this.localStorageService.getJsonAsync(indexedDetailsKey, null);

				if (parsed && typeof parsed === 'object') {
					break;
				}
			}
		}

		if (!parsed || typeof parsed !== 'object') {
			return null;
		}

		return {
			ruleDetails: parsed.ruleDetails && typeof parsed.ruleDetails === 'object' ? parsed.ruleDetails : {},
			failedSelectorsByRule: parsed.failedSelectorsByRule && typeof parsed.failedSelectorsByRule === 'object' ? parsed.failedSelectorsByRule : {},
			rulePartsByRule: parsed.rulePartsByRule && typeof parsed.rulePartsByRule === 'object' ? parsed.rulePartsByRule : {}
		};
	}

	async loadRuleDetailsForPage(rulesResultsDetailsStorageKey, pageUrl) {
		const bundle = await this.loadRuleDataBundleForPage(rulesResultsDetailsStorageKey, pageUrl);
		return bundle?.ruleDetails || null;
	}

	mergeCrawledPagesWithRuleResults(crawledPages, ruleResultPages) {
		const normalizedCrawledPages = this.normalizeCrawledPages(crawledPages);
		const normalizedRulePages = this.ruleRunner
			? this.ruleRunner.normalizePages(Array.isArray(ruleResultPages) ? ruleResultPages : [])
			: [];
		const rulesByUrl = new Map(normalizedRulePages.map((page) => [page.url, page]));

		return normalizedCrawledPages.map((crawledPage) => {
			const existingRulePage = rulesByUrl.get(crawledPage.url);

			if (!existingRulePage) {
				if (!this.ruleRunner) {
					return { ...crawledPage };
				}

				const [defaultPage] = this.ruleRunner.normalizePages([{ ...crawledPage }]);
				return defaultPage;
			}

			return {
				...existingRulePage,
				depth: crawledPage.depth,
				postId: crawledPage.postId ?? this.normalizePostId(existingRulePage.postId),
				pageType: this.normalizePageType(crawledPage.pageType ?? existingRulePage.pageType),
				crawledAt: crawledPage.crawledAt
			};
		});
	}

	syncCrawledPagesWithRulePostIds(crawledPages, ruleResultPages) {
		const normalizedCrawledPages = this.normalizeCrawledPages(crawledPages);
		const normalizedRulePages = this.ruleRunner
			? this.ruleRunner.normalizePages(Array.isArray(ruleResultPages) ? ruleResultPages : [])
			: [];
		const rulePostIdByUrl = new Map();

		for (const rulePage of normalizedRulePages) {
			const normalizedPostId = this.normalizePostId(rulePage.postId);

			if (normalizedPostId) {
				rulePostIdByUrl.set(rulePage.url, normalizedPostId);
			}
		}

		return normalizedCrawledPages.map((crawledPage) => ({
			...crawledPage,
			postId: crawledPage.postId ?? rulePostIdByUrl.get(crawledPage.url) ?? null
		}));
	}

	updateCrawledPagePostId(crawledPages, pageUrl, postId) {
		const normalizedPostId = this.normalizePostId(postId);

		if (!normalizedPostId) {
			return crawledPages;
		}

		const normalizedPageUrl = this.normalizeUrl(pageUrl);
		return crawledPages.map((page) => {
			if (this.normalizeUrl(page.url) !== normalizedPageUrl) {
				return page;
			}

			return {
				...page,
				postId: normalizedPostId
			};
		});
	}

	saveCrawledPages(crawledPathsStorageKey, pages) {
		this.localStorageService.setJson(crawledPathsStorageKey, this.normalizeCrawledPages(pages));
	}

	saveRuleResultPageDetails(rulesResultsDetailsStorageKey, page) {
		const detailsStorageKey = this.getRuleDetailsPageStorageKey(rulesResultsDetailsStorageKey, page?.url);

		if (!detailsStorageKey) {
			return;
		}

		this.localStorageService.setJson(detailsStorageKey, {
			ruleDetails: page?.ruleDetails || {},
			failedSelectorsByRule: page?.failedSelectorsByRule || {},
			rulePartsByRule: page?.rulePartsByRule || {}
		});
	}

	saveRuleResultPagesSummary(rulesResultsStorageKey, pages) {
		const normalizedPages = this.ruleRunner
			? this.ruleRunner.normalizePages(Array.isArray(pages) ? pages : [])
			: (Array.isArray(pages) ? pages : []);

		this.localStorageService.setJson(rulesResultsStorageKey, normalizedPages);
	}

	saveRuleResultPages(rulesResultsStorageKey, pages, rulesResultsDetailsStorageKey = '') {
		const normalizedPages = this.ruleRunner
			? this.ruleRunner.normalizePages(Array.isArray(pages) ? pages : [])
			: (Array.isArray(pages) ? pages : []);

		if (!rulesResultsDetailsStorageKey) {
			this.localStorageService.setJson(rulesResultsStorageKey, normalizedPages);
			return;
		}

		const indexStorageKey = this.getRuleDetailsIndexStorageKey(rulesResultsDetailsStorageKey);
		const previousDetailsKeys = this.localStorageService.getJson(indexStorageKey, []);
		const nextDetailsKeys = [];
		normalizedPages.forEach((page) => {
			const pageDetailsStorageKey = this.getRuleDetailsPageStorageKey(rulesResultsDetailsStorageKey, page.url);

			if (pageDetailsStorageKey) {
				nextDetailsKeys.push(pageDetailsStorageKey);
				this.localStorageService.setJson(pageDetailsStorageKey, {
					ruleDetails: page?.ruleDetails || {},
					failedSelectorsByRule: page?.failedSelectorsByRule || {},
					rulePartsByRule: page?.rulePartsByRule || {}
				});
			}
		});

		const nextDetailsKeySet = new Set(nextDetailsKeys);

		for (const previousDetailsKey of Array.isArray(previousDetailsKeys) ? previousDetailsKeys : []) {
			if (!nextDetailsKeySet.has(previousDetailsKey)) {
				this.localStorageService.remove(previousDetailsKey);
			}
		}

		this.localStorageService.setJson(indexStorageKey, nextDetailsKeys);
		this.localStorageService.setJson(rulesResultsStorageKey, normalizedPages);
	}

	removeRuleResultDetails(rulesResultsDetailsStorageKey) {
		const indexStorageKey = this.getRuleDetailsIndexStorageKey(rulesResultsDetailsStorageKey);
		const detailsKeys = this.localStorageService.getJson(indexStorageKey, []);

		for (const detailsKey of Array.isArray(detailsKeys) ? detailsKeys : []) {
			this.localStorageService.remove(detailsKey);
		}

		this.localStorageService.remove(indexStorageKey);
	}
}
